import React from "react";
import { Button, Card, Col, Container, Row } from "react-bootstrap";
import { Navigate, useParams } from "react-router-dom";
import { Link } from "react-router-dom";

import { SEOHead } from "../Components/Common/SEOHead";
import { pathTitles, useStore } from "../Store/Store";

// City-specific data
const cityData = {
  chicago: {
    name: "Chicago",
    state: "IL",
    stateFull: "Illinois",
    distance: "3 hours",
    driveTime: "3-hour drive",
    description:
      "Chicago's #1 source for professionally trained puppies delivered to your door",
    benefits: [
      "Quick 3-hour delivery from Indianapolis",
      "Perfect for Chicago apartments - already house & crate trained",
      "No need to travel - we bring your trained puppy to you",
      "Ideal for busy Chicago professionals",
    ],
    testimonial: {
      text: "We live in downtown Chicago and couldn't be happier with our trained puppy from Todo's Translator. She was perfectly behaved from day one!",
      author: "Sarah M., Chicago IL",
    },
    faqs: [
      {
        question: "Are your puppies suitable for Chicago apartments?",
        answer:
          "Absolutely! All our puppies are already house-trained and crate-trained, making them perfect for apartment living. They're also noise-desensitized to urban sounds like sirens, elevators, and traffic, which is essential for Chicago city life.",
      },
      {
        question: "How do trained puppies handle Chicago winters?",
        answer:
          "Our puppies are trained to potty outside in various weather conditions, including cold and snow. We recommend winter gear like booties and coats for smaller breeds during harsh Chicago winters. All puppies are comfortable with quick outdoor potty breaks even in freezing temperatures.",
      },
      {
        question: "Are they trained for leash walking on busy sidewalks?",
        answer:
          "Yes! Our puppies receive leash training and are socialized to walk calmly in busy environments. They're comfortable with crowds, other dogs, and the hustle and bustle of Chicago streets.",
      },
      {
        question: "Can I take my puppy on the CTA or in Ubers?",
        answer:
          "Our puppies are crate-trained and socialized to various environments, making them great candidates for public transportation. We recommend starting with short trips and using a secure carrier for CTA rides.",
      },
      {
        question: "What's included with delivery to Chicago?",
        answer:
          "Your puppy comes fully trained (house, crate, basic obedience), health certified, with vaccination records, a training handoff session, and lifetime training support. We deliver directly to your Chicago address with a 3-hour drive from Indianapolis.",
      },
    ],
  },
  denver: {
    name: "Denver",
    state: "CO",
    stateFull: "Colorado",
    distance: "14 hours",
    driveTime: "flight or drive",
    description:
      "Denver's premier source for professionally trained puppies with nationwide delivery",
    benefits: [
      "Professional flight nanny delivery to Denver",
      "Perfect for Colorado's active lifestyle - already trained",
      "3-day training weekend included with delivery",
      "Ideal for Denver's dog-friendly culture",
    ],
    testimonial: {
      text: "Our trained puppy arrived in Denver perfectly socialized and ready to hit the trails. The delivery process was seamless!",
      author: "Mike R., Denver CO",
    },
    faqs: [
      {
        question: "Are your puppies ready for Colorado's outdoor lifestyle?",
        answer:
          "Yes! Our puppies are well-socialized and have basic obedience training, making them great companions for hiking, camping, and outdoor adventures. They're comfortable with car rides and new environments, perfect for Colorado's active lifestyle.",
      },
      {
        question: "How do they handle Denver's altitude and climate?",
        answer:
          "Puppies adapt well to altitude. We recommend gradual introduction to strenuous activities and plenty of water. Our puppies are trained to potty in various weather conditions including snow and cold, which is perfect for Denver winters.",
      },
      {
        question: "Can they eventually be off-leash at dog parks?",
        answer:
          "Our puppies have solid recall training foundations with the 'come' command. With continued training and practice in safe, enclosed areas, they can develop reliable off-leash skills. Denver has excellent dog parks for this!",
      },
      {
        question:
          "Are they good with other dogs for Denver's social dog culture?",
        answer:
          "Absolutely! All our puppies are extensively socialized with other dogs and are comfortable in group settings. They're perfect for Denver's dog-friendly breweries, patios, and parks.",
      },
      {
        question: "What's the delivery process to Denver?",
        answer:
          "We use professional flight nanny service to Denver International Airport (DEN). Your puppy travels safely in-cabin with a dedicated handler. We can also arrange ground delivery for an extended training handoff experience.",
      },
    ],
  },
  minneapolis: {
    name: "Minneapolis-St. Paul",
    state: "MN",
    stateFull: "Minnesota",
    distance: "9 hours",
    driveTime: "9-hour drive or flight",
    description:
      "Twin Cities' premier source for professionally trained puppies",
    benefits: [
      "Personal delivery to Minneapolis-St. Paul metro area",
      "Perfect for Minnesota families - fully socialized & trained",
      "Cold-weather ready and crate trained",
      "Ideal for active Twin Cities lifestyle",
    ],
    testimonial: {
      text: "We got our trained puppy delivered to Minneapolis and she's been perfect from day one. Worth every penny!",
      author: "Mark S., Minneapolis MN",
    },
    faqs: [
      {
        question: "Can your puppies handle Minnesota winters?",
        answer:
          "Yes! Our puppies are trained to potty outside in cold weather and snow. We recommend winter gear like booties and coats for smaller or short-haired breeds during harsh Minnesota winters. All puppies adapt well to cold climates with proper care.",
      },
      {
        question: "Are they good for families with children?",
        answer:
          "Absolutely! All our puppies are extensively socialized with people of all ages. They have gentle temperaments and basic obedience training, making them wonderful family companions. We always recommend supervised interactions with young children.",
      },
      {
        question: "Do they need a lot of exercise in winter months?",
        answer:
          "Exercise needs vary by breed, but all dogs need daily activity regardless of weather. Our puppies are crate-trained and house-trained, making indoor time easier. Short, frequent outdoor breaks work well in extreme cold, supplemented with indoor play.",
      },
      {
        question:
          "Are they comfortable with car rides for Minnesota road trips?",
        answer:
          "Yes! Our puppies are socialized to car rides and crate-trained, making them great travel companions for trips to the cabin, dog parks, or family visits around the Twin Cities.",
      },
      {
        question: "What's the delivery process to Minneapolis?",
        answer:
          "We offer personal delivery to the Minneapolis-St. Paul metro area via a 9-hour drive from Indianapolis, or we can arrange flight nanny service to MSP airport. Both options include a training handoff session.",
      },
    ],
  },
  "san-francisco": {
    name: "San Francisco Bay Area",
    state: "CA",
    stateFull: "California",
    distance: "flight",
    driveTime: "professional flight nanny delivery",
    description:
      "Bay Area's premier source for professionally trained puppies with white-glove delivery",
    benefits: [
      "Professional flight nanny delivery to SFO, OAK, or SJC airports",
      "Perfect for Bay Area apartments and condos - already house & crate trained",
      "Ideal for busy tech professionals who value their time",
      "Skip the puppy training phase - get a fully trained companion",
    ],
    testimonial: {
      text: "Living in San Francisco, we needed a puppy that was already trained. Our puppy arrived perfectly behaved and ready for city life!",
      author: "David L., San Francisco CA",
    },
    faqs: [
      {
        question:
          "Are your puppies good for San Francisco apartments and condos?",
        answer:
          "Yes! Our puppies are specifically trained for apartment living with complete house training and crate training. They're comfortable in small spaces and are noise-desensitized to urban sounds like cable cars, fog horns, and street traffic.",
      },
      {
        question: "Do you have hypoallergenic breeds available?",
        answer:
          "We work with various breeds including hypoallergenic options. Contact us to discuss which breeds we currently have available that would be a good fit for allergies and your Bay Area lifestyle.",
      },
      {
        question: "How does flight delivery to the Bay Area work?",
        answer:
          "We use professional flight nannies who hand-deliver your puppy to SFO, OAK, or SJC airports. Your puppy travels in-cabin (not cargo) for maximum safety and comfort. We coordinate pickup times that work with your schedule.",
      },
      {
        question: "Are they socialized for dog parks and city environments?",
        answer:
          "Absolutely! All our puppies are extensively socialized with other dogs and people. They're comfortable in busy environments and are great candidates for SF's many dog parks and dog-friendly cafes.",
      },
      {
        question:
          "What if I work long hours in tech - can I still have a puppy?",
        answer:
          "Our trained puppies are perfect for busy professionals! They're already crate-trained for safe alone time, house-trained to prevent accidents, and have basic obedience. This eliminates the most time-intensive puppy training phase, though all puppies still need daily exercise and attention.",
      },
    ],
  },
  seattle: {
    name: "Seattle",
    state: "WA",
    stateFull: "Washington",
    distance: "flight",
    driveTime: "professional flight nanny delivery",
    description:
      "Seattle's trusted source for professionally trained puppies delivered to your door",
    benefits: [
      "Professional flight nanny delivery to SEA airport",
      "Perfect for Seattle's outdoor lifestyle - already trained for adventures",
      "Ideal for Pacific Northwest dog culture",
      "Rain-trained and ready for Seattle weather",
    ],
    testimonial: {
      text: "Our trained puppy arrived in Seattle ready to explore the trails. The delivery was seamless and professional!",
      author: "Emily R., Seattle WA",
    },
    faqs: [
      {
        question: "Are your puppies comfortable with Seattle's rainy weather?",
        answer:
          "Yes! Our puppies are trained to potty outside in various weather conditions including rain. While some puppies naturally prefer dry weather, they're all comfortable with wet conditions and won't refuse to go outside when it's raining.",
      },
      {
        question:
          "Do they need special grooming for the Pacific Northwest climate?",
        answer:
          "Grooming needs depend on the breed. We provide detailed care instructions for your specific puppy. Regular brushing and occasional professional grooming keeps most breeds comfortable in Seattle's damp climate.",
      },
      {
        question:
          "Are they good for Seattle apartments or do they need a yard?",
        answer:
          "Our puppies are trained for both apartments and homes with yards. They're house-trained and crate-trained, making them perfect for Seattle's urban apartments. Regular walks and exercise are important regardless of living situation.",
      },
      {
        question: "Can they join me on hikes and outdoor adventures?",
        answer:
          "Absolutely! Our puppies are socialized to various environments and have basic obedience training. They're great companions for Pacific Northwest adventures like hiking, beach trips, and exploring Seattle's many dog-friendly parks.",
      },
      {
        question: "How does delivery to Seattle work?",
        answer:
          "We use professional flight nanny service to Seattle-Tacoma International Airport (SEA). Your puppy travels in-cabin with a dedicated handler for maximum safety and comfort. We coordinate pickup times that work with your schedule.",
      },
    ],
  },
  nashville: {
    name: "Nashville",
    state: "TN",
    stateFull: "Tennessee",
    distance: "5 hours",
    driveTime: "5-hour drive",
    description:
      "Nashville's trusted source for professionally trained puppies delivered to your home",
    benefits: [
      "Easy 5-hour delivery from Indianapolis",
      "Perfect for Nashville families - fully socialized & trained",
      "Personal delivery with training handoff",
      "Great for Music City's active lifestyle",
    ],
    testimonial: {
      text: "We got our trained puppy delivered to Nashville and the whole process was amazing. She's the perfect addition to our family!",
      author: "Jessica T., Nashville TN",
    },
    faqs: [
      {
        question: "Are your puppies good with children and families?",
        answer:
          "Yes! Our puppies are extensively socialized and have gentle, friendly temperaments perfect for Nashville families. They're trained in basic obedience and are comfortable with active households. We always recommend supervised interactions with young children.",
      },
      {
        question: "Do they do well in homes with yards?",
        answer:
          "Absolutely! While our puppies are house-trained and crate-trained for any living situation, they love having yard space to play. They're trained to potty outside and will quickly learn your yard routine.",
      },
      {
        question: "How do they handle Nashville's hot, humid summers?",
        answer:
          "Our puppies adapt well to various climates. For Nashville summers, we recommend early morning or evening walks, plenty of water, and air-conditioned indoor time during peak heat. Breed selection can also help - ask us about heat-tolerant breeds.",
      },
      {
        question: "Are they comfortable with visitors and social gatherings?",
        answer:
          "Yes! Our puppies are well-socialized to new people and environments. They're friendly and well-mannered, making them great for Nashville's social, dog-friendly culture and family gatherings.",
      },
      {
        question: "What's included with delivery to Nashville?",
        answer:
          "Your puppy comes fully trained (house, crate, basic obedience), health certified, with vaccination records, and a personal training handoff session. Delivery is just a 5-hour drive from Indianapolis, and we include lifetime training support.",
      },
    ],
  },
  columbus: {
    name: "Columbus",
    state: "OH",
    stateFull: "Ohio",
    distance: "2 hours",
    driveTime: "2-hour drive",
    description:
      "Columbus' #1 source for professionally trained puppies with quick delivery",
    benefits: [
      "Quick 2-hour delivery from Indianapolis",
      "Perfect for Columbus families and young professionals",
      "Personal delivery with training handoff",
      "Great for Ohio's family-oriented lifestyle",
    ],
    testimonial: {
      text: "We're in Columbus and the delivery was so easy. Our puppy was perfectly trained and ready to join our family!",
      author: "Amanda K., Columbus OH",
    },
    faqs: [
      {
        question: "What training do your puppies receive?",
        answer:
          "All our puppies receive 8 weeks of professional training including house training (no accidents!), crate training (sleeps through the night), basic obedience (sit, stay, come, down), and extensive socialization with people and other dogs.",
      },
      {
        question: "Are they good for families with children?",
        answer:
          "Yes! Our puppies are extensively socialized with people of all ages and have gentle, friendly temperaments. They're trained in basic obedience and make wonderful family companions. We always recommend supervised interactions with young children.",
      },
      {
        question: "What's included with my puppy?",
        answer:
          "Your puppy comes fully trained, health certified, with up-to-date vaccinations, deworming, microchip, health guarantee, training records, and lifetime training support. We also provide a training handoff session to ensure smooth transition.",
      },
      {
        question: "Can I see the puppy before committing?",
        answer:
          "Yes! We offer video calls so you can meet your puppy virtually before delivery. We provide regular photo and video updates during the training period so you can watch your puppy grow and learn.",
      },
      {
        question: "What happens after delivery?",
        answer:
          "We provide lifetime training support! If you have any questions or need guidance as your puppy grows, we're always here to help. We want to ensure long-term success for both you and your puppy.",
      },
    ],
  },
  charlotte: {
    name: "Charlotte",
    state: "NC",
    stateFull: "North Carolina",
    distance: "8 hours",
    driveTime: "8-hour drive or flight",
    description:
      "Charlotte's trusted source for professionally trained puppies",
    benefits: [
      "Professional delivery to Charlotte metro area",
      "Perfect for Queen City families - fully trained & socialized",
      "Ideal for Charlotte's growing dog-friendly culture",
      "Ready for Southern hospitality and outdoor adventures",
    ],
    testimonial: {
      text: "Our trained puppy arrived in Charlotte perfectly behaved. The whole process was professional and easy!",
      author: "Jennifer W., Charlotte NC",
    },
    faqs: [
      {
        question: "What training do your puppies receive?",
        answer:
          "All our puppies receive 8 weeks of professional training including house training (no accidents!), crate training (sleeps through the night), basic obedience (sit, stay, come, down), and extensive socialization with people and other dogs.",
      },
      {
        question: "Are they good for families with children?",
        answer:
          "Yes! Our puppies are extensively socialized with people of all ages and have gentle, friendly temperaments. They're trained in basic obedience and make wonderful family companions. We always recommend supervised interactions with young children.",
      },
      {
        question: "What's included with my puppy?",
        answer:
          "Your puppy comes fully trained, health certified, with up-to-date vaccinations, deworming, microchip, health guarantee, training records, and lifetime training support. We also provide a training handoff session to ensure smooth transition.",
      },
      {
        question: "Can I see the puppy before committing?",
        answer:
          "Yes! We offer video calls so you can meet your puppy virtually before delivery. We provide regular photo and video updates during the training period so you can watch your puppy grow and learn.",
      },
      {
        question: "What happens after delivery?",
        answer:
          "We provide lifetime training support! If you have any questions or need guidance as your puppy grows, we're always here to help. We want to ensure long-term success for both you and your puppy.",
      },
    ],
  },
  austin: {
    name: "Austin",
    state: "TX",
    stateFull: "Texas",
    distance: "flight",
    driveTime: "flight delivery or long drive",
    description: "Austin's premier source for professionally trained puppies",
    benefits: [
      "Professional flight nanny delivery to AUS airport",
      "Perfect for Austin's dog-obsessed culture - already trained",
      "Ideal for Texas heat - crate trained for indoor comfort",
      "Ready for Austin's active, outdoor lifestyle",
    ],
    testimonial: {
      text: "Austin is so dog-friendly, and our trained puppy fit right in. She was ready for the trails from day one!",
      author: "Chris M., Austin TX",
    },
    faqs: [
      {
        question: "How do your puppies handle Austin's hot summers?",
        answer:
          "Our puppies adapt well to warm climates. For Austin's heat, we recommend early morning or late evening walks, plenty of shade and water, and air-conditioned indoor time during peak heat hours. Ask us about heat-tolerant breeds that thrive in Texas weather.",
      },
      {
        question: "Are they ready for Austin's dog-friendly culture?",
        answer:
          "Absolutely! Our puppies are extensively socialized and comfortable in various environments. They're perfect for Austin's dog-friendly patios, breweries, parks, and trails. They have solid leash manners and are friendly with other dogs.",
      },
      {
        question: "Can they go on hikes and outdoor adventures?",
        answer:
          "Yes! Our puppies have basic obedience training and are socialized to new environments, making them great companions for Austin's greenbelt trails, Zilker Park, and outdoor activities. Start with shorter hikes and build up their endurance.",
      },
      {
        question: "Are they good for apartments or do they need a yard?",
        answer:
          "Our puppies are house-trained and crate-trained, making them suitable for both apartments and homes with yards. Many Austin residents successfully raise dogs in apartments with regular exercise and outdoor time.",
      },
      {
        question: "How does delivery to Austin work?",
        answer:
          "We use professional flight nanny service to Austin-Bergstrom International Airport (AUS). Your puppy travels in-cabin with a dedicated handler for safety and comfort. We coordinate pickup times that work with your schedule.",
      },
    ],
  },
  boston: {
    name: "Boston",
    state: "MA",
    stateFull: "Massachusetts",
    distance: "flight",
    driveTime: "professional flight nanny delivery",
    description: "Boston's premier source for professionally trained puppies",
    benefits: [
      "Professional flight nanny delivery to BOS airport",
      "Perfect for Boston apartments and condos - already house trained",
      "Ideal for busy professionals who value quality training",
      "Ready for New England's four seasons",
    ],
    testimonial: {
      text: "We live in downtown Boston and needed a trained puppy. She arrived perfectly behaved and ready for city life!",
      author: "Rachel B., Boston MA",
    },
    faqs: [
      {
        question: "Are your puppies suitable for Boston apartments?",
        answer:
          "Yes! Our puppies are house-trained and crate-trained, making them perfect for Boston's apartments and condos. They're noise-desensitized to urban sounds and comfortable in small spaces, ideal for city living.",
      },
      {
        question: "How do they handle New England winters?",
        answer:
          "Our puppies are trained to potty outside in cold weather and snow. For harsh Boston winters, we recommend winter gear like booties and coats for smaller breeds. All puppies adapt well to cold climates with proper care.",
      },
      {
        question: "Are they good for busy professionals?",
        answer:
          "Absolutely! Our trained puppies are perfect for busy schedules. They're already crate-trained for safe alone time and house-trained to prevent accidents. This eliminates the most time-intensive training phase, though all puppies still need daily exercise and attention.",
      },
      {
        question: "Can they use the T or ride in cars/Ubers?",
        answer:
          "Our puppies are crate-trained and socialized to various environments. With a secure carrier, they can adapt to public transportation. They're also comfortable with car rides, making them great for getting around Boston.",
      },
      {
        question: "What's included with delivery to Boston?",
        answer:
          "Your puppy comes fully trained (house, crate, basic obedience), health certified, with vaccination records, and lifetime training support. We use professional flight nanny service to Logan Airport (BOS) with in-cabin travel for maximum safety.",
      },
    ],
  },
  "new-york": {
    name: "New York City",
    state: "NY",
    stateFull: "New York",
    distance: "flight",
    driveTime: "professional flight nanny delivery",
    description:
      "NYC's premier source for professionally trained puppies delivered to Manhattan and beyond",
    benefits: [
      "Professional flight nanny delivery to JFK, LGA, or EWR airports",
      "Perfect for Manhattan apartments - already house & crate trained",
      "Ideal for busy NYC professionals",
      "Ready for city life, elevators, crowds, and subway sounds",
    ],
    testimonial: {
      text: "Living in Manhattan, we needed a puppy that was already trained. Our puppy arrived perfectly behaved and ready for NYC!",
      author: "Alex P., New York NY",
    },
    faqs: [
      {
        question: "Are your puppies ready for NYC apartment living?",
        answer:
          "Absolutely! Our puppies are house-trained and crate-trained, perfect for Manhattan apartments. They're noise-desensitized to urban sounds like sirens, elevators, subway rumbles, and street traffic - essential for NYC living.",
      },
      {
        question: "Can they handle elevators and crowded sidewalks?",
        answer:
          "Yes! Our puppies are extensively socialized to busy environments, crowds, and various surfaces. They have solid leash manners and are comfortable with the hustle and bustle of NYC streets, making them ideal city companions.",
      },
      {
        question: "How do I potty train in NYC without a yard?",
        answer:
          "Our puppies are already house-trained to go outside on command. In NYC, they'll quickly learn to potty on sidewalk tree beds or designated areas. Some owners also use indoor potty solutions for convenience, which trained puppies adapt to easily.",
      },
      {
        question: "Are they okay on the subway or in taxis?",
        answer:
          "Our puppies are crate-trained and socialized to various environments. With a secure carrier (required for subway), they adapt well to public transportation. They're also comfortable with car rides for taxis and Ubers.",
      },
      {
        question: "What's included with delivery to NYC?",
        answer:
          "Your puppy comes fully trained (house, crate, basic obedience), health certified, with vaccination records, and lifetime training support. We use professional flight nanny service to JFK, LGA, or EWR with in-cabin travel for maximum safety.",
      },
    ],
  },
  greenville: {
    name: "Greenville",
    state: "SC",
    stateFull: "South Carolina",
    distance: "7 hours",
    driveTime: "7-hour drive",
    description:
      "Greenville's trusted source for professionally trained puppies",
    benefits: [
      "Personal delivery to Greenville metro area",
      "Perfect for South Carolina families - fully trained & socialized",
      "Ideal for Greenville's growing community",
      "Ready for Southern living and outdoor adventures",
    ],
    testimonial: {
      text: "We got our trained puppy delivered to Greenville and couldn't be happier. She's been the perfect addition to our family!",
      author: "Laura H., Greenville SC",
    },
    faqs: [
      {
        question: "What training do your puppies receive?",
        answer:
          "All our puppies receive 8 weeks of professional training including house training (no accidents!), crate training (sleeps through the night), basic obedience (sit, stay, come, down), and extensive socialization with people and other dogs.",
      },
      {
        question: "Are they good for families with children?",
        answer:
          "Yes! Our puppies are extensively socialized with people of all ages and have gentle, friendly temperaments. They're trained in basic obedience and make wonderful family companions. We always recommend supervised interactions with young children.",
      },
      {
        question: "What's included with my puppy?",
        answer:
          "Your puppy comes fully trained, health certified, with up-to-date vaccinations, deworming, microchip, health guarantee, training records, and lifetime training support. We also provide a training handoff session to ensure smooth transition.",
      },
      {
        question: "Can I see the puppy before committing?",
        answer:
          "Yes! We offer video calls so you can meet your puppy virtually before delivery. We provide regular photo and video updates during the training period so you can watch your puppy grow and learn.",
      },
      {
        question: "What happens after delivery?",
        answer:
          "We provide lifetime training support! If you have any questions or need guidance as your puppy grows, we're always here to help. We want to ensure long-term success for both you and your puppy.",
      },
    ],
  },
  columbia: {
    name: "Columbia",
    state: "SC",
    stateFull: "South Carolina",
    distance: "8 hours",
    driveTime: "8-hour drive",
    description: "Columbia's trusted source for professionally trained puppies",
    benefits: [
      "Personal delivery to Columbia metro area",
      "Perfect for South Carolina's capital city families",
      "Fully trained & socialized for family life",
      "Ready for Southern living and outdoor adventures",
    ],
    testimonial: {
      text: "We got our trained puppy delivered to Columbia and she's been amazing. The training made all the difference!",
      author: "Michael D., Columbia SC",
    },
    faqs: [
      {
        question: "What training do your puppies receive?",
        answer:
          "All our puppies receive 8 weeks of professional training including house training (no accidents!), crate training (sleeps through the night), basic obedience (sit, stay, come, down), and extensive socialization with people and other dogs.",
      },
      {
        question: "Are they good for families with children?",
        answer:
          "Yes! Our puppies are extensively socialized with people of all ages and have gentle, friendly temperaments. They're trained in basic obedience and make wonderful family companions. We always recommend supervised interactions with young children.",
      },
      {
        question: "What's included with my puppy?",
        answer:
          "Your puppy comes fully trained, health certified, with up-to-date vaccinations, deworming, microchip, health guarantee, training records, and lifetime training support. We also provide a training handoff session to ensure smooth transition.",
      },
      {
        question: "Can I see the puppy before committing?",
        answer:
          "Yes! We offer video calls so you can meet your puppy virtually before delivery. We provide regular photo and video updates during the training period so you can watch your puppy grow and learn.",
      },
      {
        question: "What happens after delivery?",
        answer:
          "We provide lifetime training support! If you have any questions or need guidance as your puppy grows, we're always here to help. We want to ensure long-term success for both you and your puppy.",
      },
    ],
  },
  "champaign-urbana": {
    name: "Champaign-Urbana",
    state: "IL",
    stateFull: "Illinois",
    distance: "2.5 hours",
    driveTime: "2.5-hour drive",
    description: "Champaign-Urbana's source for professionally trained puppies",
    benefits: [
      "Quick 2.5-hour delivery from Indianapolis",
      "Perfect for college town families and professionals",
      "Personal delivery with training handoff",
      "Great for Central Illinois lifestyle",
    ],
    testimonial: {
      text: "We're in Champaign and the delivery was so convenient. Our puppy was perfectly trained and ready to go!",
      author: "Tom R., Champaign IL",
    },
    faqs: [
      {
        question: "What training do your puppies receive?",
        answer:
          "All our puppies receive 8 weeks of professional training including house training (no accidents!), crate training (sleeps through the night), basic obedience (sit, stay, come, down), and extensive socialization with people and other dogs.",
      },
      {
        question: "Are they good for families with children?",
        answer:
          "Yes! Our puppies are extensively socialized with people of all ages and have gentle, friendly temperaments. They're trained in basic obedience and make wonderful family companions. We always recommend supervised interactions with young children.",
      },
      {
        question: "What's included with my puppy?",
        answer:
          "Your puppy comes fully trained, health certified, with up-to-date vaccinations, deworming, microchip, health guarantee, training records, and lifetime training support. We also provide a training handoff session to ensure smooth transition.",
      },
      {
        question: "Can I see the puppy before committing?",
        answer:
          "Yes! We offer video calls so you can meet your puppy virtually before delivery. We provide regular photo and video updates during the training period so you can watch your puppy grow and learn.",
      },
      {
        question: "What happens after delivery?",
        answer:
          "We provide lifetime training support! If you have any questions or need guidance as your puppy grows, we're always here to help. We want to ensure long-term success for both you and your puppy.",
      },
    ],
  },
};

export const CityLandingPage = () => {
  const { city } = useParams();
  const getLink = useStore((state) => state.getLink);

  // Get city data or redirect if invalid
  const cityInfo = cityData[city?.toLowerCase()];

  if (!cityInfo) {
    return <Navigate to="/" replace />;
  }

  return (
    <>
      <SEOHead
        pageType="home"
        title={`Trained Puppies Delivered to ${cityInfo.name}, ${cityInfo.state} | Todo's Translator`}
        description={`${cityInfo.description}. Professional puppy training with ${cityInfo.driveTime} delivery to ${cityInfo.name}. House-trained, crate-trained, and socialized puppies ready for your family.`}
      />

      <Container
        className="city-landing-page small_container clear"
        style={{ paddingTop: "40px", paddingBottom: "60px" }}
      >
        {/* Hero Section */}
        <Row className="titles text-center mb-5">
          <Col>
            <Card.Title
              className="title"
              style={{ fontSize: "2.5rem", marginBottom: "20px" }}
            >
              Trained Puppies Delivered to {cityInfo.name}, {cityInfo.state}
            </Card.Title>
            <Card.Subtitle
              className="title two"
              style={{ fontSize: "1.3rem", color: "var(--cedar-dark)" }}
            >
              {cityInfo.description}
            </Card.Subtitle>
          </Col>
        </Row>

        {/* Benefits Section */}
        <Row className="mb-5">
          <Col md={6}>
            <h2 style={{ marginBottom: "25px", fontSize: "1.8rem" }}>
              Why {cityInfo.name} Families Choose Us
            </h2>
            <ul style={{ fontSize: "1.1rem", lineHeight: "2" }}>
              {cityInfo.benefits.map((benefit, index) => (
                <li key={index} style={{ marginBottom: "10px" }}>
                  <strong>✓</strong> {benefit}
                </li>
              ))}
            </ul>
          </Col>
          <Col md={6}>
            <Card
              style={{
                padding: "30px",
                backgroundColor: "var(--almond-light)",
                border: "2px solid var(--almond-dark)",
                borderRadius: "10px",
              }}
            >
              <h3 style={{ marginBottom: "20px", fontSize: "1.5rem" }}>
                What You Get
              </h3>
              <ul
                style={{
                  fontSize: "1rem",
                  lineHeight: "1.8",
                  listStyle: "none",
                  paddingLeft: "0",
                }}
              >
                <li style={{ marginBottom: "12px" }}>
                  🏠 <strong>House Trained</strong> - No accidents in your home
                </li>
                <li style={{ marginBottom: "12px" }}>
                  📦 <strong>Crate Trained</strong> - Sleeps peacefully through
                  the night
                </li>
                <li style={{ marginBottom: "12px" }}>
                  🐕 <strong>Basic Obedience</strong> - Sit, stay, come, down
                </li>
                <li style={{ marginBottom: "12px" }}>
                  👥 <strong>Fully Socialized</strong> - Great with people &
                  dogs
                </li>
                <li style={{ marginBottom: "12px" }}>
                  ✈️ <strong>Delivery Included</strong> - We bring your puppy to{" "}
                  {cityInfo.name}
                </li>
                <li style={{ marginBottom: "12px" }}>
                  📚 <strong>Training Support</strong> - Lifetime guidance
                  included
                </li>
              </ul>
            </Card>
          </Col>
        </Row>

        {/* CTA Section */}
        <Row className="mb-5 text-center">
          <Col>
            <Card
              style={{
                padding: "40px",
                border: "none",
              }}
            >
              <h2
                style={{
                  marginBottom: "20px",
                  fontSize: "2rem",
                  color: "var(--cedar-dark)",
                }}
              >
                Ready to Welcome Your Trained Puppy?
              </h2>
              <p
                style={{
                  fontSize: "1.2rem",
                  marginBottom: "30px",
                  color: "var(--cedar-dark)",
                }}
              >
                Browse our available puppies and find your perfect match
              </p>
              <div
                style={{
                  display: "flex",
                  gap: "20px",
                  justifyContent: "center",
                  flexWrap: "wrap",
                }}
              >
                <Button
                  as={Link}
                  to={getLink(pathTitles.VIEWPUPPIES)}
                  size="lg"
                  style={{
                    backgroundColor: "var(--cedar-primary)",
                    border: "none",
                    padding: "15px 40px",
                    fontSize: "1.1rem",
                    borderRadius: "8px",
                  }}
                >
                  View Available Puppies
                </Button>
                <Button
                  as={Link}
                  to={getLink(pathTitles.CONTACTUS)}
                  size="lg"
                  variant="outline-primary"
                  style={{
                    backgroundColor: "var(--almond-primary)",
                    border: "none",
                    padding: "15px 40px",
                    fontSize: "1.1rem",
                    borderRadius: "8px",
                    borderWidth: "2px",
                  }}
                >
                  Contact Us
                </Button>
              </div>
            </Card>
          </Col>
        </Row>

        {/* Testimonial Section */}
        <Row className="mb-5">
          <Col>
            <Card
              style={{
                padding: "40px",
                backgroundColor: "var(--almond-light)",
                borderRadius: "10px",
              }}
            >
              <div style={{ textAlign: "center" }}>
                <p
                  style={{
                    fontSize: "1.3rem",
                    fontStyle: "italic",
                    marginBottom: "20px",
                    color: "var(--sea-green-dark)",
                  }}
                >
                  &quot;{cityInfo.testimonial.text}&quot;
                </p>
                <p
                  style={{
                    fontSize: "1.1rem",
                    fontWeight: "bold",
                    color: "var(--sea-green-dark)",
                  }}
                >
                  — {cityInfo.testimonial.author}
                </p>
              </div>
            </Card>
          </Col>
        </Row>

        {/* How It Works Section */}
        <Row className="mb-5">
          <Col>
            <h2
              style={{
                textAlign: "center",
                marginBottom: "40px",
                fontSize: "2rem",
                color: "var(--cedar-dark)",
              }}
            >
              How Delivery to {cityInfo.name} Works
            </h2>
            <Row>
              <Col md={4} className="text-center mb-4">
                <div
                  style={{
                    width: "80px",
                    height: "80px",
                    borderRadius: "50%",
                    backgroundColor: "var(--cedar-primary)",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "2.5rem",
                    fontWeight: "bold",
                    margin: "0 auto 20px",
                    boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                  }}
                >
                  1
                </div>
                <h4
                  style={{ marginBottom: "15px", color: "var(--cedar-dark)" }}
                >
                  Choose Your Puppy
                </h4>
                <p style={{ color: "var(--text-color-light)" }}>
                  Browse our available trained puppies and select your perfect
                  match
                </p>
              </Col>
              <Col md={4} className="text-center mb-4">
                <div
                  style={{
                    width: "80px",
                    height: "80px",
                    borderRadius: "50%",
                    backgroundColor: "var(--sea-green-primary)",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "2.5rem",
                    fontWeight: "bold",
                    margin: "0 auto 20px",
                    boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                  }}
                >
                  2
                </div>
                <h4
                  style={{
                    marginBottom: "15px",
                    color: "var(--sea-green-dark)",
                  }}
                >
                  8-Week Training
                </h4>
                <p style={{ color: "var(--text-color-light)" }}>
                  Your puppy receives professional training while you prepare
                  your home
                </p>
              </Col>
              <Col md={4} className="text-center mb-4">
                <div
                  style={{
                    width: "80px",
                    height: "80px",
                    borderRadius: "50%",
                    backgroundColor: "var(--almond-dark)",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "2.5rem",
                    fontWeight: "bold",
                    margin: "0 auto 20px",
                    boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                  }}
                >
                  3
                </div>
                <h4
                  style={{ marginBottom: "15px", color: "var(--almond-dark)" }}
                >
                  Delivery to {cityInfo.name}
                </h4>
                <p style={{ color: "var(--text-color-light)" }}>
                  We deliver your fully trained puppy to your {cityInfo.name}{" "}
                  home with training handoff
                </p>
              </Col>
            </Row>
          </Col>
        </Row>

        {/* FAQ Section */}
        {cityInfo.faqs && cityInfo.faqs.length > 0 && (
          <Row className="mb-5">
            <Col>
              <h2
                style={{
                  textAlign: "center",
                  marginBottom: "40px",
                  fontSize: "2rem",
                  color: "var(--cedar-dark)",
                }}
              >
                Common Questions from {cityInfo.name} Families
              </h2>
              {cityInfo.faqs.map((faq, index) => (
                <Card
                  key={index}
                  style={{
                    marginBottom: "20px",
                    border: "1px solid var(--almond-dark)",
                    borderRadius: "8px",
                  }}
                >
                  <Card.Body>
                    <h4
                      style={{
                        fontSize: "1.3rem",
                        marginBottom: "15px",
                        color: "var(--sea-green-dark)",
                      }}
                    >
                      {faq.question}
                    </h4>
                    <p
                      style={{
                        fontSize: "1.1rem",
                        lineHeight: "1.7",
                        color: "var(--text-color-light)",
                        marginBottom: "0",
                      }}
                    >
                      {faq.answer}
                    </p>
                  </Card.Body>
                </Card>
              ))}
            </Col>
          </Row>
        )}

        {/* Final CTA */}
        <Row className="text-center">
          <Col>
            <h3 style={{ marginBottom: "20px", fontSize: "1.8rem" }}>
              Serving {cityInfo.name} Families Since 2015
            </h3>
            <p
              style={{
                fontSize: "1.2rem",
                marginBottom: "30px",
                color: "#666",
              }}
            >
              Join the ranks of happy families in {cityInfo.name} who chose a
              trained puppy
            </p>
            <Button
              as="a"
              href="sms:+13179546952"
              size="lg"
              style={{
                backgroundColor: "var(--cedar-primary)",
                border: "none",
                padding: "15px 40px",
                fontSize: "1.2rem",
                borderRadius: "8px",
              }}
            >
              Text Justine Now at (317) 954-6952
            </Button>
          </Col>
        </Row>
      </Container>
    </>
  );
};
